// screens-create-report.jsx — CreateFlow wizard + ReportScreen (multi-row)
import React from 'react';
import {
  kr, num, fmtMin, fmtHM, STATUS, STATUS_ORDER, ARTICLES, artByCode,
  TEMPLATES, JOBS, VAT, rowAmount, reportTotal, rowLabel, rowUnit, rowRate,
  TODAY, isoLocal, fmtDateLong, fmtDateShort, APP, COMPANY, uid,
  WORK_START_MIN, WORK_END_MIN, hmToMin, minToHM, ceilMin, lunchWindow, lunchAbsorb, groupParts, nextGroupId,
  travelBetween, prevJobSameDay, resolveDeparture,
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton,
  Icon, ICON_PATHS,
} from '../shared.js';
import { Empty } from './screens-main.jsx';
import { ReceiptScanSheet } from './screens-receipt.jsx';
import { SlotPickerSheet } from './screens-calendar.jsx';
const { useState: useStateC } = React;

const roundHalf = (x) => Math.round(x * 2) / 2;
// diagonal placeholder fill for photo thumbnails (mirrors screens-flow.jsx)
const STRIPE = 'repeating-linear-gradient(135deg,#eef1f1 0 9px,#e6eaeb 9px 18px)';

// recent / suggested places for the create flow (location picker mock)
const PLACES = [
  { customer: 'Familjen Andersson', address: 'Storgatan 14, 411 24 Göteborg', pin: { x: 0.42, y: 0.38 }, min: 18, km: 9.4 },
  { customer: 'BRF Solrosen', address: 'Frölundagatan 22, 431 44 Mölndal', pin: { x: 0.55, y: 0.66 }, min: 26, km: 17.8 },
  { customer: 'Café Bryggan', address: 'Kajvägen 8, 414 58 Göteborg', pin: { x: 0.48, y: 0.5 }, min: 12, km: 5.2 },
  { customer: 'Industrivägen 9', address: 'Industrivägen 9, 417 07 Göteborg', pin: { x: 0.62, y: 0.3 }, min: 21, km: 12.1 },
];
const FROM_OPTS = [
  { mode: 'auto', label: 'Automatiskt', sub: 'Verkstad eller föregående uppdrag', icon: 'sparkle' },
  { mode: 'workshop', label: 'Verkstaden', sub: 'Ringön 6', icon: 'building' },
  { mode: 'gps', label: 'Min nuvarande plats', sub: 'GPS – hämtas automatiskt', icon: 'navigate' },
];

// ── CREATE FLOW ─────────────────────────────────────────────
function CreateFlow({ onClose, onCreate, templates = TEMPLATES, defaultTemplateId, initialDate, initialTime, jobs = [], settings = {} }) {
  const [step, setStep] = useStateC(0);
  const [mode, setMode] = useStateC('planerad'); // planerad | direkt
  const [templateId, setTemplateId] = useStateC(defaultTemplateId || (templates[0] && templates[0].id));
  const [place, setPlace] = useStateC(null);
  const [fromIdx, setFromIdx] = useStateC(0);
  const [title, setTitle] = useStateC('');
  const [customer, setCustomer] = useStateC('');
  const [dateISO, setDateISO] = useStateC(initialDate || TODAY);
  const [calOpen, setCalOpen] = useStateC(false);
  const [startTime, setStartTime] = useStateC(initialTime || '09:00');
  const [estHours, setEstHours] = useStateC(2);
  const [desc, setDesc] = useStateC('');
  const [materials, setMaterials] = useStateC([]);
  const [phone, setPhone] = useStateC('');
  const [email, setEmail] = useStateC('');
  const [movedNote, setMovedNote] = useStateC('');
  const [splitOn, setSplitOn] = useStateC(false);
  const [sessions, setSessions] = useStateC([]); // [{ date, startTime, estHours }]
  const [sessCalIdx, setSessCalIdx] = useStateC(-1);

  const steps = ['Plats', 'Detaljer', 'Material'];
  const canNext = step === 0 ? !!place : step === 1 ? title.trim().length > 0 : true;

  const tomorrowISO = (() => { const d = new Date(TODAY + 'T00:00:00'); d.setDate(d.getDate() + 1); return isoLocal(d); })();
  const isToday = dateISO === TODAY, isTomorrow = dateISO === tomorrowISO;
  const isCustomDate = !isToday && !isTomorrow;

  // ── scheduling: lunch absorption + inter-job travel ──
  const toMin = hmToMin;
  const toHM = minToHM;
  const durMin = Math.round(estHours * 60);

  // travel time from the previous meeting to this place, per Google Maps (mock = place.min)
  const travelMin = (settings.travelOn === false || !place) ? 0 : (place.min || 0);

  // existing jobs on a given day (lunch is handled softly via absorption, not as a hard block)
  const bookingsOn = (iso) => jobs.filter(j => j.date === iso && j.startTime)
    .map(j => { const s = toMin(j.startTime); return s == null ? null : { s, e: s + (j.estMinutes || 0), title: j.title }; })
    .filter(Boolean).sort((a, b) => a.s - b.s);
  const dayBookings = bookingsOn(dateISO);

  const startMin = toMin(startTime);
  const effDurAt = (t) => durMin + lunchAbsorb(t, durMin, settings); // work + any lunch it runs through
  const precedingAt = (t) => { let p = null; for (const b of dayBookings) { if (b.e <= t && (!p || b.e > p.e)) p = b; } return p; };
  const overlapAt = (t) => dayBookings.find(b => t < b.e && b.s < t + effDurAt(t));
  const travelShortAt = (t) => { if (travelMin <= 0) return null; const p = precedingAt(t); return (p && (t - p.e) < travelMin) ? p : null; };
  const fits = (t) => t != null && !overlapAt(t) && !travelShortAt(t);
  const nextFit = (from) => { for (let t = ceilMin(from); t <= 22 * 60; t += 15) if (fits(t)) return t; return null; };

  const overlap = startMin == null ? null : overlapAt(startMin);
  const travelShort = startMin == null ? null : travelShortAt(startMin);
  const conflict = overlap || travelShort ? { overlap, travelShort, title: (overlap || travelShort).title } : null;
  const suggested = conflict ? nextFit(startMin) : null;
  const leadJob = (() => { const p = startMin != null ? precedingAt(startMin) : null; return (p && travelMin > 0) ? p : null; })();
  const lunchMin = startMin != null ? lunchAbsorb(startMin, durMin, settings) : 0;

  const onTimeChange = (v) => { setStartTime(v); setMovedNote(''); };
  const onTimeBlur = () => { if (conflict && suggested != null) { setStartTime(toHM(suggested)); const why = conflict.overlap ? `Krockade med ${conflict.title}` : `${travelMin} min restid från ${conflict.title}`; setMovedNote(`${why} – flyttad till ${toHM(suggested)}`); } };
  const applySuggested = () => { if (suggested != null) { setStartTime(toHM(suggested)); setMovedNote(''); } };

  // ── split-across-days sessions ──
  const nextWeekday = (iso, n = 1) => { const d = new Date(iso + 'T00:00:00'); let added = 0; while (added < n) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) added++; } return isoLocal(d); };
  const enableSplit = () => { setSplitOn(true); setSessions(s => s.length ? s : [{ date: dateISO, startTime, estHours }, { date: nextWeekday(dateISO), startTime: '08:00', estHours }]); };
  const patchSession = (i, p) => setSessions(ss => ss.map((x, j) => j === i ? { ...x, ...p } : x));
  const addSession = () => setSessions(ss => { const last = ss[ss.length - 1]; return [...ss, { date: nextWeekday(last ? last.date : dateISO), startTime: '08:00', estHours: last ? last.estHours : estHours }]; });
  const removeSession = (i) => setSessions(ss => ss.filter((_, j) => j !== i));
  const sessionInfo = (s) => { const st = toMin(s.startTime); if (st == null) return { bad: true }; const dur = Math.round(s.estHours * 60); const lm = lunchAbsorb(st, dur, settings); const ov = bookingsOn(s.date).find(b => st < b.e && b.s < st + dur + lm); return { overlap: ov, lunchMin: lm, endMin: st + dur + lm }; };
  const totalHours = sessions.reduce((s, x) => s + (x.estHours || 0), 0);

  function submit() {
    const base = {
      title: title.trim() || 'Nytt uppdrag',
      customer: customer.trim() || place.customer || 'Kund',
      phone: phone.trim(),
      email: email.trim(),
      status: 'planerad',
      address: place.address,
      pin: place.pin,
      fromMode: FROM_OPTS[fromIdx].mode,
      fromAddress: FROM_OPTS[fromIdx].mode === 'workshop' ? COMPANY.base : (FROM_OPTS[fromIdx].mode === 'gps' ? 'Min plats (GPS)' : 'Automatiskt'),
      travelMinutes: place.min,
      travelKm: place.km,
      description: desc.trim() || '—',
      materials: materials.filter(m => m.name.trim()),
      templateId,
      report: null,
    };
    if (splitOn && sessions.length > 1) {
      const parts = sessions
        .filter(s => toMin(s.startTime) != null)
        .sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime))
        .map((s) => ({ ...base, date: s.date, startTime: s.startTime, estMinutes: Math.round(s.estHours * 60) }));
      parts.forEach((p, i) => { p.partIndex = i + 1; p.partTotal = parts.length; });
      onCreate(parts, false);
      return;
    }
    const job = { ...base, date: mode === 'planerad' ? dateISO : TODAY, startTime, estMinutes: durMin };
    onCreate(job, mode === 'direkt');
  }

  return (
    <div className="gv-app">
      <AppHeader onBack={step === 0 ? onClose : () => setStep(step - 1)} subtitle={`Steg ${step + 1} av 3`} title="Nytt uppdrag" />
      {/* progress */}
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '2px 22px 14px' }}>
        {steps.map((s, i) => {
          const done = i < step, cur = i === step;
          return (
            <React.Fragment key={s}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 58 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 13, background: (done || cur) ? 'var(--accent)' : 'var(--surface)', color: (done || cur) ? '#fff' : 'var(--ink-3)', border: (done || cur) ? 'none' : '1.5px solid var(--line-2)' }}>
                  {done ? <Icon name="check" size={15} stroke={3} color="#fff" /> : i + 1}
                </div>
                <span style={{ fontSize: 11, fontWeight: 650, marginTop: 6, color: cur ? 'var(--ink)' : 'var(--ink-3)', textAlign: 'center', lineHeight: 1.2 }}>{s}</span>
              </div>
              {i < steps.length - 1 && <div style={{ flex: 1, height: 2, borderRadius: 2, background: i < step ? 'var(--accent)' : 'var(--line-2)', marginTop: 13 }} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="gv-scroll has-footer" style={{ paddingTop: 4 }}>
        {step === 0 && (
          <div className="gv-fade">
            <SecLabel>När utförs uppdraget?</SecLabel>
            <div style={{ display: 'flex', gap: 7 }}>
              {[['planerad', 'Planera', 'calendar'], ['direkt', 'Rapportera nu', 'doc']].map(([k, l, ic]) => (
                <button key={k} onClick={() => setMode(k)} className="gv-card" style={{ flex: 1, padding: '0 10px', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, border: mode === k ? '1.5px solid var(--accent)' : '1px solid var(--line)', background: mode === k ? 'var(--accent-wash)' : 'var(--surface)', cursor: 'pointer' }}>
                  <Icon name={ic} size={18} stroke={2} color={mode === k ? 'var(--accent-ink)' : 'var(--ink-2)'} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: mode === k ? 'var(--accent-ink)' : 'var(--ink-2)' }}>{l}</div>
                </button>
              ))}
            </div>

            <SecLabel>Plats för uppdraget</SecLabel>
            <MapMock pin={place ? place.pin : { x: 0.5, y: 0.45 }} height={104} label={place ? place.address : 'Välj en plats nedan'} />
            <div className="gv-input-wrap" style={{ marginTop: 10 }}>
              <Icon name="search" size={18} stroke={1.9} color="var(--ink-3)" />
              <input className="gv-input" placeholder="Sök adress eller välj på kartan" value={place ? place.address : ''} onChange={() => {}} readOnly />
            </div>
            <SecLabel>Senaste platser</SecLabel>
            <div className="gv-list">
              {PLACES.map((p, i) => (
                <div key={i} className="gv-lrow is-tap" onClick={() => { setPlace(p); if (!customer) setCustomer(p.customer); }}>
                  <div className="gv-lrow-ic" style={{ background: place === p ? 'var(--accent-wash)' : 'var(--surface-2)', color: place === p ? 'var(--accent-ink)' : 'var(--ink-2)' }}><Icon name="pin" size={18} stroke={1.9} /></div>
                  <div className="gv-lrow-tt"><b style={{ fontSize: 14.5, fontWeight: 600 }}>{p.customer}</b><span>{p.address}</span></div>
                  {place === p ? <Icon name="checkCircle" size={20} color="var(--accent)" stroke={2} /> : <Icon name="chevR" size={16} color="var(--ink-3)" stroke={2} />}
                </div>
              ))}
            </div>

            <SecLabel>Utgår från</SecLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FROM_OPTS.map((f, i) => (
                <button key={i} onClick={() => setFromIdx(i)} className="gv-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', textAlign: 'left', border: fromIdx === i ? '1.5px solid var(--accent)' : '1px solid var(--line)', background: fromIdx === i ? 'var(--accent-wash)' : 'var(--surface)', cursor: 'pointer' }}>
                  <div className="gv-lrow-ic" style={{ background: fromIdx === i ? '#fff' : 'var(--surface-2)', color: fromIdx === i ? 'var(--accent-ink)' : 'var(--ink-2)' }}><Icon name={f.icon} size={18} stroke={1.9} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: fromIdx === i ? 'var(--accent-ink)' : 'var(--ink)' }}>{f.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{f.sub}</div>
                  </div>
                  {fromIdx === i && <Icon name="checkCircle" size={19} color="var(--accent)" stroke={2} />}
                </button>
              ))}
            </div>

            {place && (FROM_OPTS[fromIdx].mode === 'auto' ? (
              <Card style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 13, background: 'var(--surface-2)' }}>
                <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="route" size={19} stroke={1.9} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>Restid beräknas vid rapport</div>
                  <div style={{ fontSize: 13.5, fontWeight: 650, lineHeight: 1.35, marginTop: 1 }}>Från verkstaden eller det uppdrag du kommer från den dagen.</div>
                </div>
              </Card>
            ) : (
              <Card style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 13, background: 'var(--surface-2)' }}>
                <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="car" size={19} stroke={1.9} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>Föreslagen restid {FROM_OPTS[fromIdx].mode === 'gps' ? 'från din plats' : 'från verkstaden'}</div>
                  <div className="gv-tnum" style={{ fontSize: 17, fontWeight: 760 }}>{place.min} min · {num(place.km, 1)} km</div>
                </div>
                <button className="gv-iconbtn is-soft" aria-label="Uppdatera"><Icon name="route" size={18} stroke={1.9} /></button>
              </Card>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="gv-fade" style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <Field label="Rubrik" value={title} onChange={setTitle} placeholder="t.ex. Byte av varmvattenberedare" />
            <Field label="Kund" value={customer} onChange={setCustomer} placeholder="Kundens namn" />
            <Field label="Telefon" value={phone} onChange={setPhone} placeholder="070-123 45 67" type="tel" mono />
            <Field label="E-post" value={email} onChange={setEmail} placeholder="namn@exempel.se" type="email" />
            {mode === 'planerad' && (
              <div className="gv-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="gv-lrow" style={{ justifyContent: 'space-between', borderBottom: 'none' }}>
                  <div className="gv-lrow-ic" style={splitOn ? { background: 'var(--accent-wash)', color: 'var(--accent-ink)' } : null}><Icon name="layers" size={19} stroke={1.9} /></div>
                  <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14.5, fontWeight: 650, display: 'block' }}>Dela upp på flera dagar</b><span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>Ett uppdrag, flera besök</span></div>
                  <Toggle on={splitOn} onChange={(v) => v ? enableSplit() : setSplitOn(false)} />
                </div>
              </div>
            )}

            {!splitOn ? (
              <>
                <div>
                  <span className="gv-label" style={{ display: 'block', marginBottom: 7 }}>Datum</span>
                  <div style={{ display: 'flex', gap: 7 }}>
                    <button onClick={() => setDateISO(TODAY)} className={'gv-chip' + (isToday ? ' is-on' : '')} style={{ flex: 1, justifyContent: 'center', height: 40 }}>Idag</button>
                    <button onClick={() => setDateISO(tomorrowISO)} className={'gv-chip' + (isTomorrow ? ' is-on' : '')} style={{ flex: 1, justifyContent: 'center', height: 40 }}>Imorgon</button>
                    <button onClick={() => setCalOpen(true)} className={'gv-chip' + (isCustomDate ? ' is-on' : '')} style={{ flex: 1.2, justifyContent: 'center', gap: 6, height: 40 }}>
                      <Icon name="calendar" size={15} stroke={2} />
                      {isCustomDate ? fmtDateShort(dateISO) : 'Välj datum'}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}><Field label="Starttid" value={startTime} onChange={onTimeChange} onBlur={onTimeBlur} mono /></div>
                  <div style={{ flex: 1 }}>
                    <span className="gv-label" style={{ display: 'block', marginBottom: 7 }}>Beräknad tid</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Stepper value={estHours} onChange={setEstHours} step={0.5} min={0.5} />
                      <span className="gv-affix" style={{ flexShrink: 0 }}>tim</span>
                    </div>
                  </div>
                </div>
                {conflict && (() => { const cb = conflict.overlap || conflict.travelShort; const isTravel = !conflict.overlap && conflict.travelShort; return (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 13px', borderRadius: 12, background: 'var(--accent-wash)', border: '1px solid var(--accent)' }}>
                    <Icon name={isTravel ? 'car' : 'clock'} size={18} stroke={2} color="var(--accent-ink)" style={{ marginTop: 1, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--accent-ink)' }}>{isTravel ? 'Hinner inte resa hit i tid' : 'Krockar med befintligt uppdrag'}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 1 }}>{isTravel ? `Google Maps: ${travelMin} min från ${cb.title} (slutar ${toHM(cb.e)}).` : `${cb.title} är inbokad ${toHM(cb.s)}–${toHM(cb.e)}.`}</div>
                      {suggested != null && <button onClick={applySuggested} className="gv-btn is-primary is-sm" style={{ marginTop: 9 }}><Icon name="arrowR" size={15} stroke={2.2} /> {isTravel ? `Starta ${toHM(suggested)} (inkl. restid)` : `Använd nästa lediga tid · ${toHM(suggested)}`}</button>}
                    </div>
                  </div>
                ); })()}
                {leadJob && !conflict && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--accent-ink)', fontWeight: 600 }}>
                    <Icon name="car" size={15} stroke={2} color="var(--accent-ink)" /> {travelMin} min restid från {leadJob.title} inräknad före start
                  </div>
                )}
                {lunchMin > 0 && !conflict && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--accent-ink)', fontWeight: 600 }}>
                    <Icon name="sun" size={15} stroke={2} color="var(--accent-ink)" /> Passerar lunch – reserverar {fmtMin(durMin + lunchMin)} totalt ({lunchMin} min lunch under passet)
                  </div>
                )}
                {movedNote && !conflict && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ok-ink)', fontWeight: 600 }}>
                    <Icon name="checkCircle" size={15} stroke={2} color="var(--ok-ink)" /> {movedNote}
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span className="gv-label">Deltillfällen · {sessions.length} dagar · totalt {num(totalHours, totalHours % 1 ? 1 : 0)} tim</span>
                {sessions.map((s, i) => { const info = sessionInfo(s); return (
                  <Card key={i} style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 24, height: 24, borderRadius: 7, background: 'var(--accent-wash)', color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 12 }}>{i + 1}</span>
                        <b style={{ fontSize: 14 }}>Besök {i + 1}</b>
                      </div>
                      {sessions.length > 2 && <button className="gv-iconbtn is-soft" style={{ width: 28, height: 28 }} onClick={() => removeSession(i)} aria-label="Ta bort"><Icon name="trash" size={15} stroke={1.9} color="var(--ink-3)" /></button>}
                    </div>
                    <button onClick={() => setSessCalIdx(i)} className="gv-chip" style={{ width: '100%', justifyContent: 'flex-start', gap: 8, height: 40, marginBottom: 8 }}>
                      <Icon name="calendar" size={15} stroke={2} /> {fmtDateShort(s.date)}
                    </button>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}><Field label="Starttid" value={s.startTime} onChange={(v) => patchSession(i, { startTime: v })} mono /></div>
                      <div style={{ flex: 1 }}>
                        <span className="gv-label" style={{ display: 'block', marginBottom: 7 }}>Tid</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Stepper value={s.estHours} onChange={(v) => patchSession(i, { estHours: Math.max(0.5, v) })} step={0.5} min={0.5} size="sm" />
                          <span className="gv-affix">tim</span>
                        </div>
                      </div>
                    </div>
                    {info.overlap && <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 9, fontSize: 12, color: 'var(--accent-ink)', fontWeight: 650 }}><Icon name="clock" size={14} stroke={2} /> Krockar med {info.overlap.title} ({toHM(info.overlap.s)}–{toHM(info.overlap.e)})</div>}
                    {info.lunchMin > 0 && !info.overlap && <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 9, fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}><Icon name="sun" size={14} stroke={2} /> Inkl. {info.lunchMin} min lunch · slutar {toHM(info.endMin)}</div>}
                  </Card>
                ); })}
                <button onClick={addSession} className="gv-card" style={{ width: '100%', padding: 13, border: '1.5px dashed var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--accent-ink)', fontWeight: 700, fontSize: 14, background: 'var(--surface)' }}>
                  <Icon name="plusCircle" size={19} stroke={2} /> Lägg till dag
                </button>
              </div>
            )}
            <Field label="Beskrivning" value={desc} onChange={setDesc} placeholder="Vad ska göras?" multiline />
          </div>
        )}

        {step === 2 && (
          <div className="gv-fade" style={{ paddingTop: 8 }}>
            <SecLabel action={defaultTemplateId === templateId ? 'Standard' : null}>Rapportmall · förifylls vid rapport</SecLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {templates.map(t => (
                <button key={t.id} onClick={() => setTemplateId(t.id)} className="gv-card" style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', border: templateId === t.id ? '1.5px solid var(--accent)' : '1px solid var(--line)', background: templateId === t.id ? 'var(--accent-wash)' : 'var(--surface)' }}>
                  <div className="gv-lrow-ic" style={{ background: templateId === t.id ? '#fff' : 'var(--surface-2)', color: 'var(--accent-ink)' }}><Icon name="layers" size={18} stroke={1.9} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700 }}>{t.name}{defaultTemplateId === t.id && <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--accent-ink)', marginLeft: 7 }}>STANDARD</span>}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{t.rows.map(c => artByCode(c).name).join(' · ')}</div>
                  </div>
                  {templateId === t.id && <Icon name="checkCircle" size={20} color="var(--accent)" stroke={2} />}
                </button>
              ))}
            </div>
            <SecLabel action="Lägg till" onAction={() => setMaterials([...materials, { name: '', qty: 1, unit: 'st' }])}>Material som behövs (valfritt)</SecLabel>
            {materials.length === 0 && <Empty icon="box" title="Inget material tillagt" sub="Lägg till delar du behöver ta med." />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {materials.map((m, i) => (
                <Card key={i} style={{ padding: 12 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input className="gv-input" style={{ flex: 1, padding: '8px 0', fontSize: 15 }} placeholder="Materialnamn" value={m.name}
                      onChange={e => setMaterials(materials.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                    <Stepper value={m.qty} onChange={(v) => setMaterials(materials.map((x, j) => j === i ? { ...x, qty: v } : x))} min={1} />
                    <button className="gv-iconbtn is-soft" onClick={() => setMaterials(materials.filter((_, j) => j !== i))}><Icon name="trash" size={18} stroke={1.9} color="var(--ink-2)" /></button>
                  </div>
                </Card>
              ))}
            </div>
            <Card style={{ marginTop: 16, background: 'var(--surface-2)', fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              {mode === 'planerad'
                ? 'Uppdraget sparas som planerat och hamnar i din lista för vald dag.'
                : 'Uppdraget skapas och du tas direkt vidare till rapporteringen.'}
            </Card>
          </div>
        )}
      </div>

      <div className="gv-footer">
        {step > 0 && <Btn variant="secondary" onClick={() => setStep(step - 1)} style={{ flex: 1 }}>Tillbaka</Btn>}
        {step < 2
          ? <Btn variant="primary" icon="arrowR" disabled={!canNext} onClick={() => setStep(step + 1)} style={{ flex: 1.4 }}>Nästa</Btn>
          : <Btn variant="primary" icon="check" onClick={submit} style={{ flex: 1.4 }}>{mode === 'planerad' ? 'Skapa uppdrag' : 'Skapa & rapportera'}</Btn>}
      </div>

      <CalendarSheet open={calOpen} onClose={() => setCalOpen(false)} value={dateISO} onSelect={setDateISO} minISO={TODAY} />
      <CalendarSheet open={sessCalIdx >= 0} onClose={() => setSessCalIdx(-1)} value={sessCalIdx >= 0 ? sessions[sessCalIdx].date : TODAY} onSelect={(iso) => { if (sessCalIdx >= 0) patchSession(sessCalIdx, { date: iso }); }} minISO={TODAY} />
    </div>
  );
}

// ── REPORT SCREEN (multi-row) ───────────────────────────────
function smartQty(job, code) {
  if (code === 'ARB') return roundHalf(job.estMinutes / 60);
  if (code === 'RES') return roundHalf(job.travelMinutes / 60);
  if (code === 'KM') return Math.round(job.travelKm * 2);
  return code === 'MTRL' ? 0 : 1;
}
// Seed report rows from the job's chosen template (or its standard).
// Article rows start at sensible quantities so reporting is just tapping +/-.
function buildRowsFromTemplate(job, tpl) {
  const codes = (tpl && tpl.rows && tpl.rows.length) ? tpl.rows : ['ARB', 'RES', 'KM'];
  const rows = [];
  codes.forEach(code => {
    const a = artByCode(code);
    if (code === 'MTRL' && job.materials && job.materials.length) {
      job.materials.forEach(m => rows.push({ id: uid('r'), code: 'MTRL', qty: m.qty, rate: 0, label: m.name, note: '' }));
      return;
    }
    rows.push({ id: uid('r'), code, qty: smartQty(job, code), rate: a.kind === 'material' ? 0 : undefined, note: code === 'KM' ? 'Tur & retur' : '' });
  });
  return rows;
}

function ReportScreen({ job, onBack, onSave, onFollowUp, template, templates = TEMPLATES, settings = {}, jobs = [] }) {
  const dep = resolveDeparture(job, jobs);
  const effJob = { ...job, travelMinutes: dep.min, travelKm: dep.km };
  const seeded = job.report && job.report.rows && job.report.rows.length ? job.report.rows : buildRowsFromTemplate(effJob, template);
  const [rows, setRows] = useStateC(() => seeded.map(r => ({ ...r, id: r.id || uid('r') })));
  const [photos, setPhotos] = useStateC((job.report && job.report.photos) ? job.report.photos.slice() : []);
  const [notes, setNotes] = useStateC((job.report && job.report.notes) || '');
  const [addOpen, setAddOpen] = useStateC(false);
  const [addTab, setAddTab] = useStateC('mall');
  const [scanOpen, setScanOpen] = useStateC(false);
  const [followOpen, setFollowOpen] = useStateC(false);
  const importRows = (newRows) => setRows(rs => [...rs, ...newRows]);

  const sum = rows.reduce((s, r) => s + rowAmount(r), 0);
  const vat = sum * VAT;

  const patch = (id, p) => setRows(rs => rs.map(r => r.id === id ? { ...r, ...p } : r));
  const remove = (id) => setRows(rs => rs.filter(r => r.id !== id));
  const addArticle = (code) => { const a = artByCode(code); setRows(rs => [...rs, { id: uid('r'), code, qty: a.kind === 'distance' ? 1 : 1, rate: (a.kind === 'material') ? 0 : undefined, note: '' }]); setAddOpen(false); };
  const addTemplate = (tpl) => { setRows(rs => [...rs, ...tpl.rows.map(code => ({ id: uid('r'), code, qty: code === 'ARB' ? roundHalf(job.estMinutes / 60) : 1, rate: artByCode(code).kind === 'material' ? 0 : undefined, note: '' }))]); setAddOpen(false); };

  return (
    <div className="gv-app">
      <AppHeader onBack={onBack} subtitle={job.id} title="Rapportera"
        right={<button className="gv-iconbtn" aria-label="Spara utkast" onClick={onBack}><Icon name="check" size={20} stroke={2.1} /></button>} />
      <div className="gv-scroll has-footer">
        <div style={{ fontSize: 16.5, fontWeight: 720, marginTop: 2 }}>{job.title}</div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{job.customer}</div>

        {!job.report && (
          <Card style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface-2)' }}>
            <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name={dep.basis === 'previous' ? 'route' : dep.basis === 'gps' ? 'navigate' : 'building'} size={19} stroke={1.9} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>Restid beräknad {dep.basis === 'previous' ? 'från föregående uppdrag' : dep.basis === 'gps' ? 'från din plats' : 'från verkstaden'}</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{dep.label}</div>
              <div className="gv-tnum" style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 1 }}>{dep.min} min · {num(dep.km, 1)} km — förifyllt på restid & km</div>
            </div>
          </Card>
        )}

        {/* PHOTOS */}
        <SecLabel action={photos.length ? `${photos.length} st` : null}>Foton</SecLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 11, background: STRIPE, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="camera" size={20} stroke={1.7} color="var(--ink-3)" />
              <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', background: 'var(--ink)', color: '#fff', border: '2px solid var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><Icon name="x" size={11} stroke={2.6} /></button>
            </div>
          ))}
          <button onClick={() => setPhotos([...photos, 'p' + Date.now()])} style={{ aspectRatio: '1', borderRadius: 11, border: '1.5px dashed var(--line-2)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: 'var(--accent-ink)' }}>
            <Icon name="camera" size={22} stroke={1.9} />
            <span style={{ fontSize: 10, fontWeight: 700 }}>Lägg till</span>
          </button>
        </div>

        {/* ROWS */}
        <SecLabel action="Lägg till rad" onAction={() => setAddOpen(true)}>Rapportrader</SecLabel>
        <button onClick={() => setScanOpen(true)} className="gv-card is-tap" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', marginBottom: 9, border: '1.5px solid var(--accent)', background: 'var(--accent-wash)', cursor: 'pointer' }}>
          <div className="gv-lrow-ic" style={{ background: '#fff', color: 'var(--accent-ink)' }}><Icon name="receipt" size={20} stroke={1.9} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 740, color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', gap: 6 }}>Skanna kvitto <span className="gv-badge is-sm" style={{ background: '#fff', color: 'var(--accent-ink)' }}><Icon name="sparkle" size={10} stroke={2} />AI</span></div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>Fota kvitto/faktura – fyll i materialrader automatiskt</div>
          </div>
          <Icon name="chevR" size={18} color="var(--accent-ink)" stroke={2} />
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {rows.map(r => <RowCard key={r.id} row={r} onPatch={patch} onRemove={remove} />)}
        </div>
        <button onClick={() => setAddOpen(true)} className="gv-card" style={{ width: '100%', marginTop: 9, padding: 13, border: '1.5px dashed var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--accent-ink)', fontWeight: 700, fontSize: 14.5, background: 'var(--surface)' }}>
          <Icon name="plusCircle" size={19} stroke={2} /> Lägg till rad eller mall
        </button>

        {/* NOTES */}
        <SecLabel>Anteckningar</SecLabel>
        <Field value={notes} onChange={setNotes} placeholder="Utfört arbete, avvikelser, nästa steg…" multiline />

        {/* FOLLOW-UP */}
        {onFollowUp && (
          <button onClick={() => setFollowOpen(true)} className="gv-card is-tap" style={{ width: '100%', marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer' }}>
            <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="layers" size={20} stroke={1.9} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 720 }}>Boka uppföljning</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>Inte klar? Spara rapporten och boka ett nytt besök på samma uppdrag.</div>
            </div>
            <Icon name="chevR" size={18} color="var(--ink-3)" stroke={2} />
          </button>
        )}

        {/* TOTAL */}
        <Card style={{ marginTop: 18, padding: 0, overflow: 'hidden' }}>
          <TotalRow label="Summa exkl. moms" value={kr(sum)} />
          <TotalRow label={`Moms (${Math.round(VAT * 100)}%)`} value={kr(vat)} muted />
          <div style={{ height: 1, background: 'var(--line)' }} />
          <TotalRow label="Att fakturera" value={kr(sum + vat)} big />
        </Card>
      </div>

      <div className="gv-footer">
        <Btn variant="primary" icon="flag" full onClick={() => onSave({ rows, photos, notes })}>
          {job.status === 'rapporterad' || job.status === 'fakturerad' ? 'Spara ändringar' : 'Markera som rapporterad'}
        </Btn>
      </div>

      {/* ADD ROW SHEET */}
      <Sheet open={addOpen} onClose={() => setAddOpen(false)} title="Lägg till">
        <div style={{ display: 'flex', gap: 7, background: 'var(--surface-2)', padding: 4, borderRadius: 11, marginBottom: 12 }}>
          {[['mall', 'Mallar'], ['artikel', 'Enskild artikel']].map(([k, l]) => (
            <button key={k} onClick={() => setAddTab(k)} style={{ flex: 1, height: 36, borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13.5, background: addTab === k ? 'var(--surface)' : 'transparent', color: addTab === k ? 'var(--ink)' : 'var(--ink-3)', boxShadow: addTab === k ? '0 1px 3px rgba(0,0,0,.08)' : 'none' }}>{l}</button>
          ))}
        </div>
        {addTab === 'mall' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, paddingBottom: 8 }}>
            {templates.map(t => (
              <button key={t.id} className="gv-card is-tap" onClick={() => addTemplate(t)} style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer' }}>
                <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="layers" size={19} stroke={1.9} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{t.name}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{t.desc} · {t.rows.length} {t.rows.length === 1 ? 'rad' : 'rader'}</div>
                </div>
                <Icon name="plusCircle" size={21} stroke={2} color="var(--accent)" />
              </button>
            ))}
          </div>
        ) : (
          <div className="gv-list" style={{ marginBottom: 8 }}>
            {ARTICLES.map(a => (
              <div key={a.code} className="gv-lrow is-tap" onClick={() => addArticle(a.code)}>
                <div className="gv-lrow-ic"><Icon name={a.icon} size={18} stroke={1.9} /></div>
                <div className="gv-lrow-tt"><b style={{ fontSize: 14.5, fontWeight: 600 }}>{a.name}</b><span className="gv-mono">{a.code} · {a.rate ? kr(a.rate) : 'à-pris'}/{a.unit}</span></div>
                <Icon name="plusCircle" size={21} stroke={2} color="var(--accent)" />
              </div>
            ))}
          </div>
        )}
      </Sheet>

      <ReceiptScanSheet open={scanOpen} onClose={() => setScanOpen(false)} onImport={importRows} />
      {onFollowUp && (
        <SlotPickerSheet
          open={followOpen}
          onClose={() => setFollowOpen(false)}
          title="Boka uppföljning"
          subtitle={`${job.title} · ${job.customer}. Rapporten sparas och ett nytt besök läggs till samma uppdrag.`}
          jobs={jobs}
          settings={settings}
          excludeId={job.id}
          defaultHours={(job.estMinutes || 120) / 60}
          ctaLabel="Boka uppföljning"
          onPick={(iso, startHM, hrs) => { setFollowOpen(false); onFollowUp({ rows, photos, notes }, { date: iso, startTime: startHM, hours: hrs }); }}
        />
      )}
    </div>
  );
}

function TotalRow({ label, value, muted, big }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: big ? '14px 16px' : '12px 16px', background: big ? 'var(--accent-wash)' : 'transparent' }}>
      <span style={{ fontSize: big ? 15 : 14, fontWeight: big ? 760 : 600, color: muted ? 'var(--ink-3)' : 'var(--ink)' }}>{label}</span>
      <span className="gv-tnum" style={{ fontSize: big ? 19 : 15, fontWeight: big ? 800 : 700, color: big ? 'var(--accent-ink)' : muted ? 'var(--ink-2)' : 'var(--ink)' }}>{value}</span>
    </div>
  );
}

function RowCard({ row, onPatch, onRemove }) {
  const a = artByCode(row.code);
  const editable = a.kind === 'material' || a.kind === 'cost';
  const rate = rowRate(row);
  const step = a.kind === 'time' ? 0.5 : 1;
  return (
    <Card style={{ padding: '9px 11px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <div className="gv-lrow-ic" style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0 }}><Icon name={a.icon} size={16} stroke={1.9} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {row.code === 'MTRL'
            ? <input className="gv-input" style={{ padding: 0, fontSize: 14.5, fontWeight: 650 }} placeholder="Materialnamn" value={row.label || ''} onChange={e => onPatch(row.id, { label: e.target.value })} />
            : <div style={{ fontSize: 14.5, fontWeight: 650, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rowLabel(row)}</div>}
          <div className="gv-mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
            <span>{row.code}</span><span>·</span>
            {editable
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>à
                  <input className="is-mono" style={{ width: 40, border: 'none', borderBottom: '1px solid var(--line-2)', background: 'none', outline: 'none', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-2)', textAlign: 'right', padding: '0 1px 1px' }} inputMode="numeric" value={row.rate != null ? row.rate : ''} placeholder="0" onChange={e => onPatch(row.id, { rate: parseFloat(e.target.value.replace(',', '.')) || 0 })} />kr/{a.unit}
                </span>
              : <span>à {kr(rate)}/{a.unit}</span>}
          </div>
        </div>
        <Stepper value={row.qty} onChange={v => onPatch(row.id, { qty: v })} step={step} min={0} size="sm" />
        <span className="gv-tnum" style={{ fontSize: 14, fontWeight: 760, minWidth: 52, textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap' }}>{kr(rowAmount(row))}</span>
        <button className="gv-iconbtn is-soft" style={{ width: 28, height: 28, flexShrink: 0 }} onClick={() => onRemove(row.id)} aria-label="Ta bort"><Icon name="trash" size={15} stroke={1.9} color="var(--ink-3)" /></button>
      </div>
    </Card>
  );
}

export { CreateFlow, ReportScreen, RowCard, TotalRow, buildRowsFromTemplate, smartQty, PLACES };
